import { useEffect, useMemo, useState } from "react";
import {
  testSend,
  useCatalog,
  useCredentials,
  useReports,
  useRoutePreview,
  useRuns,
  useSaveSubscriptions,
  useSinkKinds,
  useSubscriptions,
  validatePredicate,
} from "../../api/hooks";
import type { SinkSpec, SubscriptionSpec, TestSendResponse } from "../../api/types";
import { useSession } from "../../app/store";
import { Empty, Spinner, errText } from "../../components/ui";
import { CodeEditor } from "../../editor/CodeEditor";
import { CredentialsDialog, FieldInput } from "./CredentialsDialog";
import "./platform.css";

type Selection = { kind: "sink"; id: string } | { kind: "sub"; id: string } | null;

const newSink = (kind: string, taken: string[]): SinkSpec => ({
  id: uniqueId(kind, taken),
  kind,
  name: null,
  credential: null,
  options: {},
  enabled: true,
});

const newSub = (taken: string[]): SubscriptionSpec => ({
  id: uniqueId("subscription", taken),
  name: null,
  description: "",
  enabled: true,
  reports: null,
  outputs: null,
  triggers: null,
  labels: null,
  tails: null,
  fleet: null,
  predicate: null,
  sinks: [],
});

function uniqueId(base: string, taken: string[]): string {
  let n = 1;
  let id = base;
  while (taken.includes(id)) id = `${base}_${++n}`;
  return id;
}

/** subscriptions.yaml editor: sinks, subscriptions (filters + predicate), routing preview, test-send. */
export function SubscriptionsPage() {
  const { projectId, toast, setDock } = useSession();
  const subs = useSubscriptions(projectId);
  const save = useSaveSubscriptions(projectId);
  const kinds = useSinkKinds();
  const [sinks, setSinks] = useState<SinkSpec[]>([]);
  const [list, setList] = useState<SubscriptionSpec[]>([]);
  const [dirty, setDirty] = useState(false);
  const [sel, setSel] = useState<Selection>(null);
  const [mode, setMode] = useState<"form" | "yaml">("form");
  const [yamlText, setYamlText] = useState("");
  const [creds, setCreds] = useState<{ kind?: string } | null>(null);
  const [previewOpen, setPreviewOpen] = useState(true);

  useEffect(() => {
    if (!subs.data) return;
    setSinks(subs.data.sinks);
    setList(subs.data.subscriptions);
    setYamlText(subs.data.yaml);
    setDirty(false);
    setSel((s) => {
      if (s?.kind === "sink" && subs.data!.sinks.some((x) => x.id === s.id)) return s;
      if (s?.kind === "sub" && subs.data!.subscriptions.some((x) => x.id === s.id)) return s;
      return subs.data!.subscriptions[0] ? { kind: "sub", id: subs.data!.subscriptions[0].id } : subs.data!.sinks[0] ? { kind: "sink", id: subs.data!.sinks[0].id } : null;
    });
  }, [subs.data]);

  const problems = useMemo(() => {
    const out: string[] = [];
    const sinkIds = new Set(sinks.map((s) => s.id));
    for (const s of sinks) {
      const spec = kinds.data?.sinks[s.kind];
      if (!spec) out.push(`sink ${s.id}: unknown kind ${s.kind}`);
      else if (spec.credential && !s.credential && !(s.kind === "webhook" && s.options.url)) out.push(`sink ${s.id}: ${spec.label} needs a credential`);
      for (const o of spec?.options ?? []) if (o.required && !s.options[o.name]) out.push(`sink ${s.id}: option '${o.label}' is required`);
    }
    for (const sub of list) {
      if (!sub.sinks.length) out.push(`subscription ${sub.id}: no sinks`);
      for (const ref of sub.sinks) if (!sinkIds.has(ref.sink)) out.push(`subscription ${sub.id}: unknown sink ${ref.sink}`);
    }
    const dup = (xs: string[]) => xs.filter((x, i) => xs.indexOf(x) !== i);
    for (const d of new Set(dup(sinks.map((s) => s.id)))) out.push(`duplicate sink id ${d}`);
    for (const d of new Set(dup(list.map((s) => s.id)))) out.push(`duplicate subscription id ${d}`);
    return out;
  }, [sinks, list, kinds.data]);

  const doSave = async () => {
    if (!projectId) return;
    try {
      const res = await save.mutateAsync(mode === "yaml" ? { yaml: yamlText } : { sinks, subscriptions: list });
      toast(`subscriptions.yaml saved (${res.sinks.length} sinks, ${res.subscriptions.length} subscriptions)`, "success");
    } catch (e) {
      toast(errText(e), "error");
    }
  };

  const update = (fn: (sinks: SinkSpec[], list: SubscriptionSpec[]) => [SinkSpec[], SubscriptionSpec[]]) => {
    const [ns, nl] = fn(sinks, list);
    setSinks(ns);
    setList(nl);
    setDirty(true);
  };

  if (!projectId) return <Empty title="Select a project" />;
  if (subs.isError) {
    return (
      <div className="pane">
        <div className="pane-head">Subscriptions</div>
        <div className="problem err" style={{ margin: 12 }}>
          {errText(subs.error)}
        </div>
        <div className="dim small" style={{ margin: 12 }}>
          Fix subscriptions.yaml in the project folder (or via the file editor) and reload.
        </div>
      </div>
    );
  }

  const selSink = sel?.kind === "sink" ? sinks.find((s) => s.id === sel.id) : undefined;
  const selSub = sel?.kind === "sub" ? list.find((s) => s.id === sel.id) : undefined;

  return (
    <div className="subs-page">
      <div className="pane-head">
        Subscriptions
        <span className="dim small mono">subscriptions.yaml</span>
        {subs.data && !subs.data.exists && !dirty ? <span className="badge">not created yet</span> : null}
        {dirty ? <span className="badge warn">unsaved</span> : null}
        {problems.length ? (
          <span className="badge err" title={problems.join("\n")}>
            {problems.length} problem{problems.length === 1 ? "" : "s"}
          </span>
        ) : null}
        <span className="spacer" />
        <div className="tabs inline">
          <button className={mode === "form" ? "on" : ""} onClick={() => setMode("form")}>
            Form
          </button>
          <button
            className={mode === "yaml" ? "on" : ""}
            onClick={() => {
              if (mode === "form" && dirty) toast("switching to YAML shows the last saved file; save the form first to keep changes", "info");
              setMode("yaml");
            }}
          >
            YAML
          </button>
        </div>
        <button className="btn sm" onClick={() => setCreds({})}>
          🔑 Credentials
        </button>
        <button className="btn sm" onClick={() => setDock(true, "deliveries")} title="Delivery history (dock)">
          Deliveries
        </button>
        <button className="btn sm primary" onClick={doSave} disabled={save.isPending || (mode === "form" && !dirty)}>
          {save.isPending ? <Spinner /> : "Save"}
        </button>
      </div>
      {mode === "yaml" ? (
        <div className="subs-yaml">
          <CodeEditor path={`${projectId}/subscriptions.yaml`} value={yamlText} onChange={(v) => (setYamlText(v), setDirty(true))} onSave={doSave} language="yaml" />
        </div>
      ) : (
        <div className="subs-body">
          <div className="subs-side">
            <div className="section-title row">
              Sinks <span className="faint">({sinks.length})</span>
              <span className="spacer" />
              <select
                className="inp sm"
                value=""
                onChange={(e) => {
                  const k = e.target.value;
                  if (!k) return;
                  const s = newSink(k, sinks.map((x) => x.id));
                  update((ss, ll) => [[...ss, s], ll]);
                  setSel({ kind: "sink", id: s.id });
                }}
                title="Add a sink"
              >
                <option value="">+ add sink…</option>
                {Object.entries(kinds.data?.sinks ?? {}).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="list">
              {sinks.map((s) => (
                <div key={s.id} className={`list-item${selSink?.id === s.id ? " sel" : ""}`} onClick={() => setSel({ kind: "sink", id: s.id })}>
                  <span className={`badge${s.enabled ? "" : " warn"}`}>{kinds.data?.sinks[s.kind]?.label ?? s.kind}</span>
                  <div className="name">
                    <div className="mono">{s.id}</div>
                    <div className="small dim">{s.name ?? sinkSummary(s)}</div>
                  </div>
                  <span className="faint small">{list.filter((x) => x.sinks.some((r) => r.sink === s.id)).length} subs</span>
                </div>
              ))}
              {!sinks.length ? <div className="dim small" style={{ padding: 8 }}>No sinks. Add an email, SFTP, FTP, webhook or folder destination.</div> : null}
            </div>
            <div className="section-title row" style={{ marginTop: 10 }}>
              Subscriptions <span className="faint">({list.length})</span>
              <span className="spacer" />
              <button
                className="btn sm"
                onClick={() => {
                  const s = newSub(list.map((x) => x.id));
                  update((ss, ll) => [ss, [...ll, s]]);
                  setSel({ kind: "sub", id: s.id });
                }}
              >
                + add
              </button>
            </div>
            <div className="list">
              {list.map((s) => (
                <div key={s.id} className={`list-item${selSub?.id === s.id ? " sel" : ""}`} onClick={() => setSel({ kind: "sub", id: s.id })}>
                  <span className={`status-dot ${s.enabled ? "done" : ""}`} title={s.enabled ? "enabled" : "disabled"} />
                  <div className="name">
                    <div>
                      <span className="mono">{s.id}</span> {s.name ? <span className="dim">{s.name}</span> : null}
                    </div>
                    <div className="small dim">{subSummary(s)}</div>
                  </div>
                  <span className="faint small">→ {s.sinks.map((r) => r.sink).join(", ") || "—"}</span>
                </div>
              ))}
              {!list.length ? (
                <div className="dim small" style={{ padding: 8 }}>
                  No subscriptions. A subscription picks which reports (by report, trigger, label, tail, fleet or a predicate over snapshot values) go to which sinks.
                </div>
              ) : null}
            </div>
            {problems.length ? (
              <div className="col" style={{ gap: 2, marginTop: 8 }}>
                {problems.map((p, i) => (
                  <div key={i} className="problem err small">
                    {p}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          <div className="subs-main">
            {selSink ? (
              <SinkEditor
                key={selSink.id}
                sink={selSink}
                projectId={projectId}
                allIds={sinks.map((s) => s.id)}
                onChange={(next) =>
                  update((ss, ll) => [
                    ss.map((s) => (s.id === selSink.id ? next : s)),
                    next.id === selSink.id ? ll : ll.map((sub) => ({ ...sub, sinks: sub.sinks.map((r) => (r.sink === selSink.id ? { ...r, sink: next.id } : r)) })),
                  ])
                }
                onDelete={() => {
                  update((ss, ll) => [ss.filter((s) => s.id !== selSink.id), ll.map((sub) => ({ ...sub, sinks: sub.sinks.filter((r) => r.sink !== selSink.id) }))]);
                  setSel(null);
                }}
                onManageCredentials={() => setCreds({ kind: kinds.data?.sinks[selSink.kind]?.credential ?? undefined })}
              />
            ) : selSub ? (
              <SubscriptionEditor
                key={selSub.id}
                sub={selSub}
                sinks={sinks}
                projectId={projectId}
                allIds={list.map((s) => s.id)}
                onChange={(next) => update((ss, ll) => [ss, ll.map((s) => (s.id === selSub.id ? next : s))])}
                onDelete={() => {
                  update((ss, ll) => [ss, ll.filter((s) => s.id !== selSub.id)]);
                  setSel(null);
                }}
              />
            ) : (
              <Empty title="Pick a sink or subscription">
                Sinks are destinations (email, SFTP, FTP, webhook, folder). Subscriptions route rendered reports to sinks.
              </Empty>
            )}
            <div className={`subs-preview${previewOpen ? "" : " closed"}`}>
              <div className="section-title row clickable" onClick={() => setPreviewOpen(!previewOpen)}>
                <span>{previewOpen ? "▾" : "▸"} Routing preview</span>
                <span className="faint small">what the saved subscriptions would do with the latest run</span>
              </div>
              {previewOpen ? <RoutingPreview projectId={projectId} /> : null}
            </div>
          </div>
        </div>
      )}
      {creds ? (
        <CredentialsDialog
          onClose={() => setCreds(null)}
          initialKind={creds.kind}
          onCreated={(id) => {
            if (selSink && !selSink.credential) update((ss, ll) => [ss.map((s) => (s.id === selSink.id ? { ...s, credential: id } : s)), ll]);
          }}
        />
      ) : null}
    </div>
  );
}

function sinkSummary(s: SinkSpec): string {
  const o = s.options;
  switch (s.kind) {
    case "email":
      return String(o.to ?? "no recipients");
    case "folder":
      return String(o.path ?? "");
    case "sftp":
    case "ftp":
      return `${s.credential ?? "no credential"} : ${o.path ?? "."}`;
    case "webhook":
      return String(o.url ?? s.credential ?? "");
    default:
      return "";
  }
}

function subSummary(s: SubscriptionSpec): string {
  const parts: string[] = [];
  if (s.reports) parts.push(`reports ${s.reports.join("|")}`);
  if (s.triggers) parts.push(`triggers ${s.triggers.join("|")}`);
  if (s.labels) parts.push(`labels ${s.labels.join("|")}`);
  if (s.outputs) parts.push(`outputs ${s.outputs.join("|")}`);
  if (s.tails) parts.push(`tails ${s.tails.join(",")}`);
  if (s.fleet) parts.push(`fleet ${s.fleet}`);
  if (s.predicate) parts.push(`if ${s.predicate}`);
  return parts.join(" · ") || "every report";
}

// ------------------------------------------------------------------------------------------------

function SinkEditor({
  sink,
  projectId,
  allIds,
  onChange,
  onDelete,
  onManageCredentials,
}: {
  sink: SinkSpec;
  projectId: string;
  allIds: string[];
  onChange: (s: SinkSpec) => void;
  onDelete: () => void;
  onManageCredentials: () => void;
}) {
  const kinds = useSinkKinds();
  const creds = useCredentials();
  const spec = kinds.data?.sinks[sink.kind];
  const [test, setTest] = useState<TestSendResponse | { error: string } | "busy" | null>(null);
  const [connOnly, setConnOnly] = useState(false);
  const idTaken = allIds.filter((i) => i !== sink.id);
  const matchingCreds = (creds.data ?? []).filter((c) => !spec?.credential || c.kind === spec.credential || c.kind === "generic");

  const run = async () => {
    setTest("busy");
    try {
      setTest(await testSend(projectId, { sink_spec: sink, connectivity_only: connOnly }));
    } catch (e) {
      setTest({ error: errText(e) });
    }
  };

  return (
    <div className="col editor-form">
      <div className="row wrap">
        <h3 className="editor-title">
          <span className="badge">{spec?.label ?? sink.kind}</span> sink
        </h3>
        <span className="spacer" />
        <label className="row small" style={{ gap: 6 }}>
          <input type="checkbox" checked={sink.enabled} onChange={(e) => onChange({ ...sink, enabled: e.target.checked })} /> enabled
        </label>
        <button className="btn sm danger" onClick={onDelete}>
          Remove
        </button>
      </div>
      <div className="row wrap">
        <label className="field">
          <span>Id</span>
          <input
            className={`inp mono${idTaken.includes(sink.id) ? " invalid" : ""}`}
            value={sink.id}
            onChange={(e) => onChange({ ...sink, id: e.target.value.replace(/[^A-Za-z0-9_.-]/g, "_") })}
          />
        </label>
        <label className="field" style={{ flex: 1 }}>
          <span>Display name</span>
          <input className="inp" value={sink.name ?? ""} onChange={(e) => onChange({ ...sink, name: e.target.value || null })} placeholder={spec?.label} />
        </label>
        {spec?.credential ? (
          <label className="field">
            <span>Credential ({spec.credential})</span>
            <span className="row" style={{ gap: 4 }}>
              <select className="inp" value={sink.credential ?? ""} onChange={(e) => onChange({ ...sink, credential: e.target.value || null })}>
                <option value="">— none —</option>
                {matchingCreds.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.id})
                  </option>
                ))}
              </select>
              <button className="btn sm" onClick={onManageCredentials} title="Manage credentials">
                🔑
              </button>
            </span>
          </label>
        ) : null}
      </div>
      <div className="section-title">Options</div>
      <div className="cred-fields">
        {(spec?.options ?? []).map((o) => (
          <FieldInput key={o.name} spec={o} value={sink.options[o.name]} onChange={(v) => onChange({ ...sink, options: setOrDrop(sink.options, o.name, v) })} />
        ))}
      </div>
      <details>
        <summary className="small dim">Advanced: extra options (JSON) — anything the sink understands, e.g. subject/body templates, mkdirs, timeout</summary>
        <JsonField value={sink.options} onChange={(v) => onChange({ ...sink, options: v })} />
      </details>
      <div className="section-title">Test</div>
      <div className="row wrap">
        <label className="row small" style={{ gap: 6 }}>
          <input type="checkbox" checked={connOnly} onChange={(e) => setConnOnly(e.target.checked)} /> connectivity only (no report sent)
        </label>
        <button className="btn sm" onClick={run} disabled={test === "busy"}>
          {test === "busy" ? <Spinner /> : connOnly ? "Test connection" : "Send test report"}
        </button>
        <span className="dim small">uses the unsaved sink definition above and the latest artifact of a finished run (or a synthetic one)</span>
      </div>
      {test && test !== "busy" ? (
        "error" in test ? (
          <div className="problem err">{test.error}</div>
        ) : (
          <div className={`problem ${test.result.ok ? "ok" : "err"}`}>
            <b>{test.result.ok ? "OK" : "failed"}</b> · {test.result.detail}
            {test.result.location ? <div className="small mono dim">{test.result.location}</div> : null}
            <div className="small dim">
              artifact {test.artifact.filename} ({test.artifact.report}/{test.artifact.output})
            </div>
          </div>
        )
      ) : null}
    </div>
  );
}

function setOrDrop(obj: Record<string, unknown>, key: string, value: unknown): Record<string, unknown> {
  const next = { ...obj };
  if (value === "" || value === undefined || value === null) delete next[key];
  else next[key] = value;
  return next;
}

function JsonField({ value, onChange }: { value: Record<string, unknown>; onChange: (v: Record<string, unknown>) => void }) {
  const [text, setText] = useState(JSON.stringify(value, null, 2));
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    setText(JSON.stringify(value, null, 2));
  }, [value]);
  return (
    <div className="col" style={{ gap: 4 }}>
      <textarea
        className={`inp mono${err ? " invalid" : ""}`}
        rows={6}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          try {
            const v = JSON.parse(text || "{}");
            if (typeof v !== "object" || Array.isArray(v) || v === null) throw new Error("must be a JSON object");
            setErr(null);
            onChange(v as Record<string, unknown>);
          } catch (e) {
            setErr(errText(e));
          }
        }}
      />
      {err ? <span className="small sev-error">{err}</span> : null}
    </div>
  );
}

// ------------------------------------------------------------------------------------------------

function SubscriptionEditor({
  sub,
  sinks,
  projectId,
  allIds,
  onChange,
  onDelete,
}: {
  sub: SubscriptionSpec;
  sinks: SinkSpec[];
  projectId: string;
  allIds: string[];
  onChange: (s: SubscriptionSpec) => void;
  onDelete: () => void;
}) {
  const reports = useReports(projectId);
  const catalog = useCatalog(projectId);
  const [pred, setPred] = useState<{ ok: boolean; error?: string } | null>(null);
  const idTaken = allIds.filter((i) => i !== sub.id);
  const reportNames = (reports.data ?? []).map((r) => r.report?.name).filter((x): x is string => !!x);
  const outputNames = [...new Set((reports.data ?? []).flatMap((r) => r.report?.outputs.map((o) => o.name) ?? []))];
  const triggerNames = (catalog.data?.triggers ?? []).map((t) => t.name);
  const labelNames = [...new Set(Object.values(catalog.data?.snapshot_labels ?? {}).flat())];

  useEffect(() => {
    if (!sub.predicate?.trim()) {
      setPred(null);
      return;
    }
    const h = setTimeout(() => validatePredicate(projectId, sub.predicate!).then(setPred).catch((e) => setPred({ ok: false, error: errText(e) })), 400);
    return () => clearTimeout(h);
  }, [sub.predicate, projectId]);

  return (
    <div className="col editor-form">
      <div className="row wrap">
        <h3 className="editor-title">subscription</h3>
        <span className="spacer" />
        <label className="row small" style={{ gap: 6 }}>
          <input type="checkbox" checked={sub.enabled} onChange={(e) => onChange({ ...sub, enabled: e.target.checked })} /> enabled
        </label>
        <button className="btn sm danger" onClick={onDelete}>
          Remove
        </button>
      </div>
      <div className="row wrap">
        <label className="field">
          <span>Id</span>
          <input
            className={`inp mono${idTaken.includes(sub.id) ? " invalid" : ""}`}
            value={sub.id}
            onChange={(e) => onChange({ ...sub, id: e.target.value.replace(/[^A-Za-z0-9_.-]/g, "_") })}
          />
        </label>
        <label className="field" style={{ flex: 1 }}>
          <span>Name</span>
          <input className="inp" value={sub.name ?? ""} onChange={(e) => onChange({ ...sub, name: e.target.value || null })} />
        </label>
        <label className="field" style={{ flex: 2 }}>
          <span>Description</span>
          <input className="inp" value={sub.description} onChange={(e) => onChange({ ...sub, description: e.target.value })} />
        </label>
      </div>

      <div className="section-title">Match (empty = any)</div>
      <div className="match-grid">
        <MultiPick label="Reports" options={reportNames} value={sub.reports} onChange={(v) => onChange({ ...sub, reports: v })} />
        <MultiPick label="Outputs" options={outputNames} value={sub.outputs} onChange={(v) => onChange({ ...sub, outputs: v })} />
        <MultiPick label="Triggers" options={triggerNames} value={sub.triggers} onChange={(v) => onChange({ ...sub, triggers: v })} />
        <MultiPick label="Snapshot labels" options={labelNames} value={sub.labels} onChange={(v) => onChange({ ...sub, labels: v })} />
        <label className="field">
          <span>Tails (comma separated, wildcards: OM-*, N?NE)</span>
          <input
            className="inp mono"
            value={(sub.tails ?? []).join(", ")}
            onChange={(e) => {
              const v = e.target.value.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean);
              onChange({ ...sub, tails: v.length ? v : null });
            }}
            placeholder="any tail"
          />
        </label>
        <label className="field">
          <span>Fleet (flight field)</span>
          <input className="inp" value={sub.fleet ?? ""} onChange={(e) => onChange({ ...sub, fleet: e.target.value || null })} placeholder="any fleet" />
        </label>
      </div>
      <label className="field">
        <span>
          Predicate — Python expression over <code>values</code> (snapshot payload), <code>tail</code>, <code>flight</code>, <code>report</code>, <code>trigger</code>, <code>label</code>,{" "}
          <code>text</code>, <code>size</code>
        </span>
        <input
          className={`inp mono${pred && !pred.ok ? " invalid" : ""}`}
          value={sub.predicate ?? ""}
          onChange={(e) => onChange({ ...sub, predicate: e.target.value || null })}
          placeholder="values.ENG1egt > 900 and tail.startswith('OM-')"
        />
        {pred ? <span className={`small ${pred.ok ? "dim" : "sev-error"}`}>{pred.ok ? "predicate compiles" : pred.error}</span> : null}
      </label>

      <div className="section-title">Deliver to</div>
      {sinks.length ? (
        <div className="col" style={{ gap: 4 }}>
          {sinks.map((s) => {
            const ref = sub.sinks.find((r) => r.sink === s.id);
            return (
              <div key={s.id} className="sink-ref">
                <label className="row small" style={{ gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={!!ref}
                    onChange={(e) =>
                      onChange({
                        ...sub,
                        sinks: e.target.checked ? [...sub.sinks, { sink: s.id, options: {} }] : sub.sinks.filter((r) => r.sink !== s.id),
                      })
                    }
                  />
                  <span className="mono">{s.id}</span>
                  <span className="badge">{s.kind}</span>
                  <span className="dim">{s.name ?? sinkSummary(s)}</span>
                  {!s.enabled ? <span className="badge warn">disabled</span> : null}
                </label>
                {ref ? (
                  <details style={{ marginLeft: 22 }}>
                    <summary className="small dim">per-subscription overrides {Object.keys(ref.options).length ? `(${Object.keys(ref.options).length})` : ""}</summary>
                    <JsonField
                      value={ref.options}
                      onChange={(v) => onChange({ ...sub, sinks: sub.sinks.map((r) => (r.sink === s.id ? { ...r, options: v } : r)) })}
                    />
                  </details>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="dim small">Add a sink first.</div>
      )}
    </div>
  );
}

function MultiPick({ label, options, value, onChange }: { label: string; options: string[]; value: string[] | null | undefined; onChange: (v: string[] | null) => void }) {
  const [extra, setExtra] = useState("");
  const all = [...new Set([...options, ...(value ?? [])])];
  return (
    <div className="field">
      <span>
        {label} {value ? <span className="faint">({value.length})</span> : <span className="faint">any</span>}
      </span>
      <div className="chips">
        {all.map((o) => {
          const on = value?.includes(o) ?? false;
          return (
            <button
              key={o}
              className={`chip${on ? " on" : ""}`}
              onClick={() => {
                const cur = value ?? [];
                const next = on ? cur.filter((x) => x !== o) : [...cur, o];
                onChange(next.length ? next : null);
              }}
            >
              {o}
            </button>
          );
        })}
        <input
          className="inp sm mono"
          style={{ width: 90 }}
          value={extra}
          placeholder="+ other"
          onChange={(e) => setExtra(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && extra.trim()) {
              onChange([...(value ?? []), extra.trim()]);
              setExtra("");
            }
          }}
        />
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------

function RoutingPreview({ projectId }: { projectId: string }) {
  const runs = useRuns(projectId);
  const [runId, setRunId] = useState<string>("");
  const preview = useRoutePreview(projectId, runId || null);
  const done = (runs.data ?? []).filter((r) => r.status === "done");
  return (
    <div className="col" style={{ gap: 6 }}>
      <div className="row">
        <select className="inp sm" value={runId} onChange={(e) => setRunId(e.target.value)}>
          <option value="">latest finished run</option>
          {done.map((r) => (
            <option key={r.id} value={r.id}>
              {r.label || r.id} · {r.summary?.outputs ?? 0} outputs
            </option>
          ))}
        </select>
        {preview.isFetching ? <Spinner /> : null}
        {preview.data?.run_id ? <span className="dim small mono">{preview.data.run_id}</span> : null}
      </div>
      {preview.data && !preview.data.run_id ? <div className="dim small">Run a batch first; the preview shows how each rendered report would be routed.</div> : null}
      {preview.data?.artifacts.length ? (
        <table className="tbl">
          <thead>
            <tr>
              <th>Flight</th>
              <th>Artifact</th>
              <th>Routes</th>
              <th>Not routed</th>
            </tr>
          </thead>
          <tbody>
            {preview.data.artifacts.map((a, i) => (
              <tr key={i}>
                <td>
                  <span className="mono">{a.tail ?? a.flight_id}</span>
                </td>
                <td>
                  <span className="mono">{a.filename}</span> <span className="faint small">{a.report}</span>
                </td>
                <td>
                  {a.routes.length ? (
                    a.routes.map((r, j) => (
                      <span key={j} className="badge ok" title={`subscription ${r.subscription}`}>
                        {r.subscription} → {r.sink}
                      </span>
                    ))
                  ) : (
                    <span className="faint">—</span>
                  )}
                </td>
                <td className="small dim">
                  {a.skipped.map((s, j) => (
                    <div key={j}>
                      <span className="mono">{s.subscription}</span>
                      {s.sink ? <span className="mono">/{s.sink}</span> : null}: {s.reason}
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}
